import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Settings, Shield, Key, Lock, Unlock, CheckCircle, AlertCircle, Eye, EyeOff, Search, ChevronDown, Users } from 'lucide-react';
import useArrowNav from '../hooks/useArrowNav';
import DraggableTabBar from '../components/DraggableTabBar';
import PrintButton from '../components/PrintButton';
import { PasswordStrengthIndicator } from './RecoveryPage';
import { NEU } from '../neuStyles';

function SettingsPage({ user }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [sqStatus, setSqStatus] = useState(null);
  const tabsRef = useRef(null);
  const { onKeyDown: handleTabKeyDown } = useArrowNav(tabsRef, 'button', { vertical: false, activateOnFocus: true });

  useEffect(() => {
    async function load() {
      try {
        const sqStat = await window.zignagenixAPI.getSecurityQuestionsStatus();
        setSqStatus(sqStat);
      } catch (err) {
        console.error('Failed to load settings:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) return <div className="page-loading">Loading settings...</div>;

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'security', label: 'Security', icon: Shield },
  ];

  return (
    <div className="page">
      <div className="page-header-row">
        <h1 className="page-title">Settings</h1>
        <PrintButton pageTitle="Settings" />
      </div>

      <DraggableTabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        prefKey="settings_tab_order"
        style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: 'none', boxShadow: 'inset 0 -2px 0 rgba(186,199,216,0.45)', paddingBottom: 2 }}
        tabStyle={(id) => ({
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
          background: 'none', border: 'none', cursor: 'pointer', fontSize: 14,
          color: activeTab === id ? '#2E8B98' : '#6C757D',
          borderBottom: activeTab === id ? '2px solid #2E8B98' : '2px solid transparent',
          fontWeight: activeTab === id ? 600 : 400,
          marginBottom: -2,
        })}
        iconSize={16}
      />

      {activeTab === 'general' && <GeneralTab />}
      {activeTab === 'security' && <SecurityTab user={user} sqStatus={sqStatus} onSqUpdate={setSqStatus} />}
    </div>
  );
}

// ─── General Tab ────────────────────────────────────────────────────────────

function GeneralTab() {
  return (
    <div className="settings-section">
      <h2 className="section-title"><Settings size={18} /> Application</h2>
      <div className="settings-grid">
        <div className="settings-item">
          <span className="settings-label">Version</span>
          <span className="settings-value">2.1.0</span>
        </div>
        <div className="settings-item">
          <span className="settings-label">Platform</span>
          <span className="settings-value">{window.zignagenixAPI?.platform || 'unknown'}</span>
        </div>
        <div className="settings-item">
          <span className="settings-label">Session Timeout</span>
          <span className="settings-value">15 minutes</span>
        </div>
        <div className="settings-item">
          <span className="settings-label">Database</span>
          <span className="settings-value">SQLite (sql.js)</span>
        </div>
        <div className="settings-item">
          <span className="settings-label">Password History</span>
          <span className="settings-value">Last 24 passwords (no reuse)</span>
        </div>
        <div className="settings-item">
          <span className="settings-label">Account Lockout</span>
          <span className="settings-value">5 failures / 30 min window</span>
        </div>
      </div>
    </div>
  );
}

// ─── Security Tab ───────────────────────────────────────────────────────────

function SecurityTab({ user, sqStatus, onSqUpdate }) {
  // RBAC UI-keying: gate on the permission the admin reset/lock handlers enforce
  // (users:update) rather than a role name, so UI visibility == backend allow.
  // (admin + sysadmin hold users:update — same set as the old role check; board
  // holds neither and is correctly excluded.)
  const isAdmin = !!user?.permissions?.includes('users:update');
  return (
    <div>
      <ChangePasswordSection />
      {isAdmin && <AdminPasswordResetSection />}
      <SecurityQuestionsSection sqStatus={sqStatus} onSqUpdate={onSqUpdate} />
    </div>
  );
}

// ─── Admin Password Reset & Lock Section (admin-only) ──────────────────────

function AdminPasswordResetSection() {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickSearch, setQuickSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const dropdownRef = useRef(null);

  const fetchUsers = useCallback(async () => {
    try {
      const result = await window.zignagenixAPI.getUsers();
      if (result && result.error) { setAllUsers([]); return; }
      const items = Array.isArray(result) ? result : (result?.data || []);
      setAllUsers(items);
    } catch (err) {
      console.error('Failed to load users for admin panel:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(''), 4000); return () => clearTimeout(t); }
  }, [success]);
  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(''), 6000); return () => clearTimeout(t); }
  }, [error]);

  const filteredUsers = allUsers
    .filter(u => {
      const status = u.status || (u.is_active ? 'active' : 'suspended');
      return status !== 'deleted';
    })
    .filter(u => {
      if (!quickSearch.trim()) return true;
      const q = quickSearch.toLowerCase();
      return (u.display_name || '').toLowerCase().includes(q)
        || (u.username || '').toLowerCase().includes(q)
        || (u.email || '').toLowerCase().includes(q);
    })
    .sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''));

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!selectedUser) { setError('Select a user first'); return; }
    if (!newPassword.trim()) { setError('Enter a new temporary password'); return; }
    setSaving(true);
    setError('');
    try {
      const result = await window.zignagenixAPI.adminResetPassword(selectedUser.id, newPassword);
      if (result?.error) { setError(result.message); setSaving(false); return; }
      setSuccess(`Password reset for "${selectedUser.display_name}" (${selectedUser.username}). They must change it on next login.`);
      setNewPassword('');
      setSelectedUser(null);
    } catch (err) {
      setError('Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  const handleLock = async () => {
    if (!selectedUser) return;
    if (!window.confirm(`Immediately lock account "${selectedUser.display_name}" (${selectedUser.username})?\n\nThis will prevent them from logging in until unlocked.`)) return;
    try {
      const result = await window.zignagenixAPI.adminLockAccount(selectedUser.id);
      if (result?.error) { setError(result.message); return; }
      setSuccess(`Account "${selectedUser.username}" has been locked`);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) { setError('Failed to lock account'); }
  };

  const handleUnlock = async () => {
    if (!selectedUser) return;
    try {
      const result = await window.zignagenixAPI.adminUnlockAccount(selectedUser.id);
      if (result?.error) { setError(result.message); return; }
      setSuccess(`Account "${selectedUser.username}" unlocked successfully`);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) { setError('Failed to unlock account'); }
  };

  const isLocked = selectedUser && (selectedUser.is_locked || (selectedUser.locked_until && new Date(selectedUser.locked_until) > new Date()));

  return (
    <div className="settings-section">
      <div style={{ borderLeft: '4px solid #DC2626', borderRadius: 12, padding: 20, background: '#fff', boxShadow: NEU.shadow.sm }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <Shield size={18} style={{ color: '#DC2626' }} />
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1E293B' }}>Admin — Reset User Password / Lock Account</h2>
          <span style={{ fontSize: 11, color: '#94A3B8', marginLeft: 4 }}>Restricted to administrators</span>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#991b1b', display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertCircle size={15} /> {error}
          </div>
        )}
        {success && (
          <div style={{ background: '#f0fdf4', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#166534', display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle size={15} /> {success}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 16, color: '#94A3B8', fontSize: 13 }}>Loading users...</div>
        ) : (
          <form onSubmit={handleResetPassword} style={{ maxWidth: 500 }}>
            {/* Searchable user dropdown */}
            <div ref={dropdownRef} style={{ position: 'relative', marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                <Users size={14} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} /> Select User
              </label>
              <div
                onClick={() => setDropdownOpen(!dropdownOpen)}
                style={{
                  padding: '10px 12px', fontSize: 14, cursor: 'pointer', borderRadius: 8,
                  border: '1px solid #E8ECF1', background: selectedUser ? '#f0fdf4' : '#fff',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                <span style={{ color: selectedUser ? '#1E293B' : '#94A3B8' }}>
                  {selectedUser
                    ? `${selectedUser.display_name} (${selectedUser.username})`
                    : 'Search and select a user...'}
                </span>
                <ChevronDown size={16} style={{ color: '#64748B', flexShrink: 0 }} />
              </div>
              {dropdownOpen && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                  background: '#fff', border: '1px solid #E8ECF1', borderRadius: 8,
                  boxShadow: '0 10px 20px rgba(0,0,0,0.12)', maxHeight: 300, overflow: 'hidden',
                  marginTop: 4,
                }}>
                  <div style={{ padding: '8px 10px', borderBottom: '1px solid #E8ECF1', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                    <div style={{ position: 'relative' }}>
                      <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
                      <input
                        autoFocus
                        type="text"
                        value={quickSearch}
                        onChange={e => setQuickSearch(e.target.value)}
                        placeholder="Type to search by name, username, or email..."
                        onClick={e => e.stopPropagation()}
                        style={{ width: '100%', padding: '8px 10px 8px 32px', fontSize: 13, border: '1px solid #E8ECF1', borderRadius: 6, outline: 'none' }}
                      />
                    </div>
                  </div>
                  <div style={{ overflowY: 'auto', maxHeight: 230 }}>
                    {filteredUsers.length === 0 ? (
                      <div style={{ padding: 16, textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>No users found</div>
                    ) : filteredUsers.map(u => {
                      const locked = u.is_locked || (u.locked_until && new Date(u.locked_until) > new Date());
                      return (
                        <div
                          key={u.id}
                          onClick={() => { setSelectedUser(u); setDropdownOpen(false); setQuickSearch(''); }}
                          style={{
                            padding: '10px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                            alignItems: 'center', borderBottom: '1px solid #F3F4F6',
                            background: selectedUser?.id === u.id ? 'rgba(0,188,212,0.06)' : 'transparent',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#F5F6FA'}
                          onMouseLeave={e => e.currentTarget.style.background = selectedUser?.id === u.id ? 'rgba(0,188,212,0.06)' : 'transparent'}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#1E293B' }}>{u.display_name}</div>
                            <div style={{ fontSize: 11, color: '#64748B' }}>{u.username} &bull; {u.email || 'no email'} &bull; {u.role}</div>
                          </div>
                          {locked && <Lock size={14} style={{ color: '#DC2626', flexShrink: 0 }} />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* New password field */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                <Key size={14} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} /> New Temporary Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Min 12 chars, upper + lower + number + special"
                  disabled={!selectedUser}
                  style={{
                    width: '100%', padding: '10px 40px 10px 12px', fontSize: 14, borderRadius: 8,
                    border: '1px solid #E8ECF1', outline: 'none', opacity: selectedUser ? 1 : 0.5,
                  }}
                />
                <button type="button" tabIndex={-1}
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {newPassword && <PasswordStrengthIndicator password={newPassword} />}
              <p style={{ margin: '6px 0 0', fontSize: 11, color: '#6b7280' }}>
                User will be required to change this on first login.
              </p>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button type="submit" className="btn-primary" disabled={!selectedUser || !newPassword.trim() || saving}
                style={{ padding: '10px 20px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, opacity: (selectedUser && newPassword.trim()) ? 1 : 0.5 }}>
                <Key size={14} /> {saving ? 'Resetting...' : 'Reset Password'}
              </button>
              {selectedUser && (
                <button type="button"
                  onClick={isLocked ? handleUnlock : handleLock}
                  style={{
                    padding: '10px 20px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
                    borderRadius: 8, border: 'none', cursor: 'pointer', color: '#fff',
                    background: isLocked ? '#16A34A' : '#DC2626',
                  }}>
                  {isLocked ? <><Unlock size={14} /> Unlock Account</> : <><Lock size={14} /> Lock Account</>}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function ChangePasswordSection() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!currentPassword) { setError('Enter your current password'); return; }
    if (!newPassword) { setError('Enter a new password'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }

    setLoading(true);
    try {
      const result = await window.zignagenixAPI.changePassword(currentPassword, newPassword);
      if (result.error) {
        setError(result.message);
      } else {
        setSuccess('Password changed successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (err) {
      setError('Password change failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="settings-section">
      <h2 className="section-title"><Key size={18} /> Change Password</h2>
      <form onSubmit={handleSubmit} style={{ maxWidth: 400 }}>
        {error && <div className="login-error" style={{ marginBottom: 12 }}><AlertCircle size={16} /><span>{error}</span></div>}
        {success && <div style={{ background: '#F8F9FA', color: '#2e7d32', padding: '10px 14px', borderRadius: 12, fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, boxShadow: NEU.shadow.sm, border: 'none' }}><CheckCircle size={16} />{success}</div>}

        <div className="login-field" style={{ marginBottom: 12 }}>
          <label className="login-label"><Lock size={14} /> Current Password</label>
          <div className="login-password-wrapper">
            <input type={showPassword ? 'text' : 'password'} className="login-input" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} disabled={loading} />
            <button type="button" className="login-password-toggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'}>
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        <div className="login-field" style={{ marginBottom: 12 }}>
          <label className="login-label"><Key size={14} /> New Password</label>
          <div className="login-password-wrapper">
            <input type={showPassword ? 'text' : 'password'} className="login-input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} disabled={loading} />
            <button type="button" className="login-password-toggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        <div className="login-field" style={{ marginBottom: 12 }}>
          <label className="login-label"><Key size={14} /> Confirm New Password</label>
          <input type={showPassword ? 'text' : 'password'} className="login-input" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} disabled={loading} />
        </div>
        <PasswordStrengthIndicator password={newPassword} />
        <button type="submit" className="login-button" style={{ marginTop: 8 }} disabled={loading}>
          {loading ? 'Changing...' : 'Change Password'}
        </button>
      </form>
    </div>
  );
}

function SecurityQuestionsSection({ sqStatus, onSqUpdate }) {
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [editing, setEditing] = useState(false);
  const [selections, setSelections] = useState([
    { questionText: '', answer: '' },
    { questionText: '', answer: '' },
    { questionText: '', answer: '' },
  ]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function loadQuestions() {
      const qs = await window.zignagenixAPI.getSecurityQuestionsList();
      setAvailableQuestions(qs);
    }
    loadQuestions();
  }, []);

  function updateSelection(index, field, value) {
    const updated = [...selections];
    updated[index] = { ...updated[index], [field]: value };
    setSelections(updated);
  }

  function getUsedQuestions(excludeIndex) {
    return selections.filter((_, i) => i !== excludeIndex).map((s) => s.questionText).filter(Boolean);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess('');

    for (let i = 0; i < 3; i++) {
      if (!selections[i].questionText) { setError(`Select question ${i + 1}`); return; }
      if (!selections[i].answer.trim() || selections[i].answer.trim().length < 2) { setError(`Answer question ${i + 1}`); return; }
    }

    const uniqueQuestions = new Set(selections.map(s => s.questionText));
    if (uniqueQuestions.size !== selections.length) {
      setError('Each security question must be different');
      return;
    }

    setLoading(true);
    try {
      const result = await window.zignagenixAPI.setupSecurityQuestions(selections);
      if (result.error) {
        setError(result.message);
      } else {
        setSuccess('Security questions updated');
        setEditing(false);
        onSqUpdate({ isSetUp: true, count: 3 });
      }
    } catch (err) {
      setError('Failed to save');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="settings-section">
      <h2 className="section-title"><Shield size={18} /> Security Questions</h2>

      {!editing ? (
        <div style={{ background: '#F8F9FA', borderRadius: 12, padding: 16, boxShadow: NEU.shadow.xs, border: 'none' }}>
          <p style={{ fontSize: 14, color: sqStatus?.isSetUp ? '#43a047' : '#e53935', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            {sqStatus?.isSetUp ? (
              <><CheckCircle size={16} /> Security questions are configured ({sqStatus.count}/3)</>
            ) : (
              <><AlertCircle size={16} /> Security questions not set up</>
            )}
          </p>
          <button className="login-button" style={{ maxWidth: 200 }} onClick={() => setEditing(true)}>
            {sqStatus?.isSetUp ? 'Update Questions' : 'Set Up Questions'}
          </button>
        </div>

      ) : (
        <form onSubmit={handleSubmit} style={{ maxWidth: 480 }}>
          {error && <div className="login-error" style={{ marginBottom: 12 }}><AlertCircle size={16} /><span>{error}</span></div>}
          {success && <div style={{ background: '#F8F9FA', color: '#2e7d32', padding: '10px 14px', borderRadius: 12, fontSize: 13, marginBottom: 12, boxShadow: NEU.shadow.sm, border: 'none' }}><CheckCircle size={16} /> {success}</div>}

          {[0, 1, 2].map((i) => {
            const used = getUsedQuestions(i);
            return (
              <div key={i} style={{ marginBottom: 12, padding: 12, background: '#F8F9FA', borderRadius: 12, border: 'none', boxShadow: NEU.shadow.xs }}>
                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block' }}>Question {i + 1}</label>
                <select className="login-input" style={{ marginBottom: 6, fontSize: 13 }} value={selections[i].questionText} onChange={(e) => updateSelection(i, 'questionText', e.target.value)}>
                  <option value="">-- Select --</option>
                  {availableQuestions.filter((q) => !used.includes(q)).map((q) => <option key={q} value={q}>{q}</option>)}
                </select>
                <input type="text" className="login-input" style={{ fontSize: 13 }} value={selections[i].answer} onChange={(e) => updateSelection(i, 'answer', e.target.value)} placeholder="Your answer (case-insensitive)" />
              </div>
            );
          })}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="login-button" disabled={loading}>{loading ? 'Saving...' : 'Save'}</button>
            <button type="button" className="login-button" style={{ background: '#6C757D' }} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}

export default SettingsPage;
